import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CustomerCartNotFound,
  ErrorCode,
  InvalidFormField,
  StoreNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import {
  CartStoreCustomer,
  StoreCustomerDetail,
  StoreCustomerSearch,
  type StoreCustomerSummary,
} from "../model/store-customer";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

/** `/shop/:shopId/store-customers`: the shop's own customers (e.g. pulled from Lightspeed) and ringing a cart up for one. */
export class StoreCustomersApi {
  constructor(private readonly t: Transport) {}

  /** Blank `q` lists the most recently updated. At most 50 come back. */
  async search(
    ctx: ApiContext,
    shopId: string,
    opts: { q?: string; limit?: number } = {},
  ): Promise<Result<StoreCustomerSummary[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/store-customers`, {
      query: { ...opts },
    });
    return forward(
      r,
      (j) => parse(StoreCustomerSearch, j).customers,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async get(
    ctx: ApiContext,
    shopId: string,
    storeCustomerId: string,
  ): Promise<Result<StoreCustomerDetail, ApiNotAuthorized | StoreNotFound | InvalidFormField>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/store-customers/${storeCustomerId}`);
    return forward(
      r,
      (j) => parse(StoreCustomerDetail, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Rings an open counter cart up for a customer; `null` detaches. */
  async attachToCart(
    ctx: ApiContext,
    shopId: string,
    customerCartId: string,
    storeCustomerId: string | null,
  ): Promise<Result<CartStoreCustomer, ApiNotAuthorized | StoreNotFound | CustomerCartNotFound | InvalidFormField>> {
    const r = await this.t.put(ctx, `/shop/${shopId}/customer-carts/${customerCartId}/store-customer`, {
      body: { storeCustomerId },
    });
    return forward(
      r,
      (j) => parse(CartStoreCustomer, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cartId" || e.incorrectValue === "status" || e.code === ErrorCode.IncorrectState)
            return new CustomerCartNotFound(customerCartId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }
}
